<script>
import { Overlay } from 'trading-vue-js'
import { mapGetters } from 'vuex'
import http from '../../services/http'
import { parseToChartCandleData } from '../../utils'

export default {
  name: 'averageDailyRangePercent',
  mixins: [Overlay],
  data() {
    return {
      lastSymId: '',
      fetchingSymId: ''
    }
  },
  computed: {
    ...mapGetters(['timeFrame', 'multiOhlcv', 'selectedTab', 'multiChartSymbol', 'multiTv']),
    sett() {
      return this.$props.settings
    },
    lineWidth() {
      return this.sett.lineWidth || 2
    },
    color() {
      return this.sett.color || '#165ba8'
    },
    period() {
      return this.sett.length || 14
    },
    symId() {
      return `adr-${this.getSymbolName}-${this.timeFrame}-${this.period}`
    },
    chartIndex() {
      return this.$attrs.tv_id ? Number(this.$attrs.tv_id.split('-')[0]) : this.selectedTab
    },
    getOhlcv() {
      return this.multiOhlcv[this.chartIndex]
    },
    getSymbolName() {
      return this.multiChartSymbol[this.chartIndex]?.symbol
    },
    market() {
      return this.multiChartSymbol[this.chartIndex]?.metaData?.exchange || ''
    },
    chartTimeframe() {
      return this.multiChartSymbol[this.chartIndex]?.timeFrame || this.timeFrame
    }
  },
  watch: {
    getOhlcv: {
      handler(newVal, oldVal) {
        if (newVal === oldVal) return
        this.init()
      },
      deep: true
    },
    period() {
      this.lastSymId = ''
      this.init()
    },
    chartTimeframe() {
      this.lastSymId = ''
      this.init()
    },
    getSymbolName() {
      this.lastSymId = ''
      this.init()
    },
    market() {
      this.lastSymId = ''
      this.init()
    },
    multiTv: {
      handler() {
        this.init()
      },
      deep: true
    }
  },
  mounted() {
    this.init()
  },
  methods: {
    meta_info() {
      return {
        author: 'Prit T. Patel',
        desc: 'Average Daily Range %',
        preset: {
          name: 'ADR% $length',
          side: 'offchart',
          settings: {
            lineWidth: 2,
            color: '#165ba8'
          }
        }
      }
    },
    use_for() {
      return ['averageDailyRangePercent', 'ADR%']
    },
    async init() {
      const symbolName = this.getSymbolName
      const timeFrame = this.chartTimeframe
      const market = this.market
      const period = this.period
      const ohlcv = this.getOhlcv

      console.log("[ADR%] init called for", symbolName, "timeframe:", timeFrame, "market:", market, "period:", period, "ohlcv length:", ohlcv ? ohlcv.length : 0, ohlcv);

      if (!symbolName || !ohlcv || ohlcv.length === 0) {
        this.clearPrecalculatedValues()
        return
      }

      const currentSymId = `${symbolName}-${timeFrame}-${market}-${period}-${ohlcv.length}`
      if (this.lastSymId === currentSymId || this.fetchingSymId === currentSymId) {
        return
      }

      this.fetchingSymId = currentSymId

      if (timeFrame === 'D') {
        this.clearPrecalculatedValues()
        this.lastSymId = currentSymId
        this.fetchingSymId = ''
      } else {
        try {
          let safeSymbolName = symbolName.replace(/%/g, '__PERCENT_PLACEHOLDER__');
          console.log("[ADR%] requesting daily data for", safeSymbolName, "market:", market);
          const res = await http.get(`/symbols/${encodeURIComponent(safeSymbolName)}`, {
            params: {
              timeFrame: 'D',
              isIntra: false,
              market: market,
              minusIndex: 0
            }
          })
          
          // Guard against stale async responses if user switched symbol or timeframe while request was in-flight
          if (this.chartTimeframe !== timeFrame || this.getSymbolName !== symbolName || this.market !== market) {
            console.log("[ADR%] Stale API response ignored due to state change.");
            return
          }

          console.log("[ADR%] API response status:", res.status);
          let dailySymbolData = (res.status === 200 && res.data && res.data.data) ? res.data.data : null;
          if (dailySymbolData && dailySymbolData.data && dailySymbolData.data.length > 0) {
            console.log("[ADR%] dailySymbolData candle count:", dailySymbolData.data.length);
            const [parsedDaily] = parseToChartCandleData(dailySymbolData.data)
            const dailyDays = this.computeADRDays(parsedDaily)
            const { mappedA, mappedC } = this.mapDailyADRToOhlcv(ohlcv, dailyDays, period)
            
            console.log("[ADR%] mappedA sample (last 5):", mappedA.slice(-5));
            console.log("[ADR%] mappedC sample (last 5):", mappedC.slice(-5));
            
            let mappedValues = [];
            if (timeFrame.startsWith('W') || timeFrame.startsWith('M')) {
              // For weekly and monthly charts, default to Methodology C to match TradingView's Pine script behavior
              mappedValues = mappedC;
            } else {
              // For intraday charts, default to Methodology A (daily ADR mapped to intraday bars)
              mappedValues = mappedA;
            }
            console.log("[ADR%] selected mappedValues sample (last 5):", mappedValues.slice(-5));
            this.updatePrecalculatedValues(mappedValues)
            this.lastSymId = currentSymId
          } else {
            console.warn("[ADR%] dailySymbolData is empty or invalid:", dailySymbolData);
            this.clearPrecalculatedValues()
            if (this.fetchingSymId === currentSymId) {
              this.fetchingSymId = ''
            }
          }
        } catch (err) {
          console.error("[ADR%] fetch error:", err)
          this.clearPrecalculatedValues()
          if (this.fetchingSymId === currentSymId) {
            this.fetchingSymId = ''
          }
        } finally {
          if (this.fetchingSymId === currentSymId) {
            this.fetchingSymId = ''
          }
        }
      }
    },
    clearPrecalculatedValues() {
      const tvCube = this.multiTv[this.chartIndex]
      if (tvCube && tvCube.data) {
        let overlayRef = tvCube.data.data.offchart ? tvCube.data.data.offchart.find(o => o.settings === this.$props.settings) : undefined
        if (!overlayRef) {
          overlayRef = tvCube.data.get_one(this.$props.id)
        }
        if (!overlayRef && tvCube.data.data && tvCube.data.data.offchart) {
          overlayRef = tvCube.data.data.offchart.find(o => o.type === 'averageDailyRangePercent' || o.type === 'ADR%')
        }
        if (overlayRef) {
          const targetId = overlayRef.id || this.$props.id
          tvCube.data.merge(`${targetId}.settings`, { adrValues: [], timeFrame: this.chartTimeframe })
          if (tvCube.tv) {
            tvCube.tv.$set(overlayRef, 'data', [])
          } else {
            overlayRef.data = []
          }
          tvCube.data.set(`${targetId}.data`, [])
        }
      }
    },
    updatePrecalculatedValues(mappedValues) {
      const tvCube = this.multiTv[this.chartIndex]
      const ohlcv = this.getOhlcv
      if (tvCube && tvCube.data && ohlcv) {
        let overlayRef = tvCube.data.data.offchart ? tvCube.data.data.offchart.find(o => o.settings === this.$props.settings) : undefined
        if (!overlayRef) {
          overlayRef = tvCube.data.get_one(this.$props.id)
        }
        if (!overlayRef && tvCube.data.data && tvCube.data.data.offchart) {
          overlayRef = tvCube.data.data.offchart.find(o => o.type === 'averageDailyRangePercent' || o.type === 'ADR%')
        }
        
        if (overlayRef) {
          const targetId = overlayRef.id || this.$props.id
          console.log("[ADR%] Found overlay in DataCube:", targetId);
          const chartData = ohlcv.map((candle, idx) => {
            const val = mappedValues[idx];
            return [candle[0], val !== undefined ? val : 0];
          })
          
          tvCube.data.merge(`${targetId}.settings`, { adrValues: mappedValues, timeFrame: this.chartTimeframe })
          if (tvCube.tv) {
            tvCube.tv.$set(overlayRef, 'data', chartData)
          } else {
            overlayRef.data = chartData
          }
          tvCube.data.set(`${targetId}.data`, chartData)
          console.log("[ADR%] Successfully set adrValues and data to DataCube path:", targetId);
        } else {
          console.warn("[ADR%] Overlay ref not found in DataCube. Printing tvCube.data.data.offchart:", tvCube.data.data ? tvCube.data.data.offchart : undefined);
        }
      } else {
        console.warn("[ADR%] DataCube or ohlcv not found for chartIndex:", this.chartIndex);
      }
    },
    computeADRDays(candles) {
      let sortedCandles = [...candles].sort((a, b) => a[0] - b[0]);
      let days = [];
      for (let i = 0; i < sortedCandles.length; i++) {
        let candle = sortedCandles[i];
        let timestamp = candle[0];
        let highVal = candle[2];
        let lowVal = candle[3];
        if (isNaN(highVal) || isNaN(lowVal) || highVal === null || lowVal === null) continue;
        days.push({
          timestamp: timestamp,
          high: highVal,
          low: lowVal,
          ratio: highVal / lowVal
        });
      }
      console.log("day",days)
      return days;
    },
    mapDailyADRToOhlcv(ohlcv, dailyDays, len) {
      if (!dailyDays || dailyDays.length === 0) {
        return { mappedA: [], mappedC: [] };
      }
      
      let sortedDailyDays = [...dailyDays].sort((a, b) => a.timestamp - b.timestamp);
      console.log("sortedDailyDays",sortedDailyDays);
      // --- METHODOLOGY A ---
      // 1. Calculate daily ADR% on daily days (20-day SMA of daily high/low ratios)
      for (let i = 0; i < sortedDailyDays.length; i++) {
        let sum = 0;
        let count = 0;
        for (let j = Math.max(0, i - len + 1); j <= i; j++) {
          sum += sortedDailyDays[j].ratio;
          count++;
        }
        let adr = sum / count;
        sortedDailyDays[i].adrPercentA = (adr - 1) * 100;
      }
      
      // 2. Map Friday's adrPercentA to the weekly candles
      let dailyIndex = 0;
      let mappedA = [];
      let defaultInterval = 86400000;
      if (ohlcv.length > 1) {
        defaultInterval = ohlcv[1][0] - ohlcv[0][0];
      }
      
      for (let i = 0; i < ohlcv.length; i++) {
        let candle = ohlcv[i];
        let t = candle[0];
        let tNext = (i + 1 < ohlcv.length) ? ohlcv[i + 1][0] : t + defaultInterval;
        
        while (dailyIndex + 1 < sortedDailyDays.length && sortedDailyDays[dailyIndex + 1].timestamp < tNext) {
          dailyIndex++;
        }
        
        let val = sortedDailyDays[dailyIndex] ? sortedDailyDays[dailyIndex].adrPercentA : 0;
        mappedA.push(val);
      }
      
      // --- METHODOLOGY C ---
      // 1. Group dailyDays into weeks and find the last daily candle of each week
      let weeklyFridayRatios = [];
      dailyIndex = 0;
      
      for (let i = 0; i < ohlcv.length; i++) {
        let candle = ohlcv[i];
        let t = candle[0];
        let tNext = (i + 1 < ohlcv.length) ? ohlcv[i + 1][0] : t + defaultInterval;
        
        while (dailyIndex + 1 < sortedDailyDays.length && sortedDailyDays[dailyIndex + 1].timestamp < tNext) {
          dailyIndex++;
        }
        
        // Check if the daily day falls within the week [t, tNext)
        let lastDaily = sortedDailyDays[dailyIndex];
        if (lastDaily && lastDaily.timestamp >= t && lastDaily.timestamp < tNext) {
          weeklyFridayRatios.push(lastDaily.ratio);
        } else {
          // Fallback to weekly candle's own high/low ratio if no daily data found for that week
          let wHigh = candle[2];
          let wLow = candle[3];
          if (wHigh && wLow && wLow !== 0) {
            weeklyFridayRatios.push(wHigh / wLow);
          } else {
            weeklyFridayRatios.push(1.0);
          }
        }
      }
      
      // 2. Calculate the SMA of weeklyFridayRatios over len periods (20 weeks)
      let mappedC = [];
      for (let i = 0; i < weeklyFridayRatios.length; i++) {
        let sum = 0;
        let count = 0;
        for (let j = Math.max(0, i - len + 1); j <= i; j++) {
          sum += weeklyFridayRatios[j];
          count++;
        }
        let adr = sum / count;
        mappedC.push((adr - 1) * 100);
      }
      
      return { mappedA, mappedC };
    },
    calc() {
      return {
        props: {
          length: { def: 14, text: 'Length' }
        },
        conf: { renderer: 'Spline' },
        init: `
            let len = typeof length !== 'undefined' ? length : 14;
            let hasPrecalculated = typeof settings.adrValues !== 'undefined' && settings.adrValues && settings.adrValues.length > 0;
            let isDaily = typeof settings.timeFrame !== 'undefined' ? settings.timeFrame === 'D' : true;
            
            if (!hasPrecalculated) {
              this.localValues = [];
              if (isDaily) {
                let ratios = [];
                for (let i = 0; i < ohlcv.length; i++) {
                  let candle = ohlcv[i];
                  let highVal = candle[2];
                  let lowVal = candle[3];
                  if (isNaN(highVal) || isNaN(lowVal) || highVal === null || lowVal === null) {
                    ratios.push(1);
                    continue;
                  }
                  ratios.push(highVal / lowVal);
                }
                
                for (let i = 0; i < ohlcv.length; i++) {
                  let sum = 0;
                  let count = 0;
                  for (let j = Math.max(0, i - len + 1); j <= i; j++) {
                    sum += ratios[j];
                    count++;
                  }
                  let adr = sum / count;
                  this.localValues.push((adr - 1) * 100);
                }
              } else {
                for (let i = 0; i < ohlcv.length; i++) {
                  this.localValues.push(0);
                }
              }
            }
        `,
        update: `
            let hasPrecalculated = typeof settings.adrValues !== 'undefined' && settings.adrValues && settings.adrValues.length > 0;
            if (hasPrecalculated) {
              let val = settings.adrValues[iter];
              return val !== undefined ? val : 0;
            } else {
              let val = this.localValues[iter];
              return val !== undefined ? val : 0;
            }
        `
      }
    }
  }
}
</script>
 